import { FC } from "react";
import { useResolveUploadedPath, useTrans } from "../../../hooks";
import { SchoolBatch, Training, TrainingYear, User } from "../../../apis";
import {
    Badge,
    HStack,
    Stack,
    VStack,
    Text,
    Box,
    Flex,
} from "@chakra-ui/react";
import { AppRenderDetailItem } from "../../../components";
import { APPC, UTIL } from "../../../config";

export const UserDetailView: FC<{
    data: User;
}> = ({ data }) => {
    const { t } = useTrans();
    const USERTYPE = APPC.BE.User.USERTYPE;
    const dayjs = UTIL.getDayJs();

    return (
        <>
            <VStack gap={8} alignItems={"stretch"}>
                <Stack alignItems={"center"} gap={3}>
                    <AppRenderDetailItem
                        renderType="IMAGE"
                        imageUrl={useResolveUploadedPath(
                            APPC.BE.Upload.FILETYPE.FILETYPE_USER_AVATAR,
                            data?.imageName,
                            undefined
                        )}
                        isAvatar={true}
                    />
                    <Badge variant={"secondary"}>
                        {t(`con.User:USERTYPE_${data?.userType}`)}
                    </Badge>
                </Stack>
                <AppRenderDetailItem
                    label={t("ent.User:email.label")}
                    value={data?.email}
                />
                <Stack
                    gap={8}
                    direction={{
                        base: "column",
                        sm: "column",
                        md: "row",
                    }}
                >
                    <AppRenderDetailItem
                        label={t("ent.User:firstName.label")}
                        value={data?.firstName}
                    />
                    <AppRenderDetailItem
                        label={t("ent.User:lastName.label")}
                        value={data?.lastName}
                    />
                </Stack>
                <Stack
                    gap={8}
                    direction={{
                        base: "column",
                        sm: "column",
                        md: "row",
                    }}
                >
                    <Box flex={1}>
                        <AppRenderDetailItem
                            label={t("ent.User:phone.label")}
                            value={data?.phone}
                        />
                        {data?.userType === USERTYPE.USERTYPE_STUDENT && (
                            <AppRenderDetailItem
                                label={t("ent.User:studentTrainer.label")}
                                value={(data?.studentTrainer as User)?.fullName}
                            />
                        )}
                    </Box>
                    <Box flex={1}>
                        {data?.userType ===
                            USERTYPE.USERTYPE_INTERNSHIP_SUPERVISOR && (
                            <AppRenderDetailItem
                                label={t("ent.User:companyServices.label")}
                            >
                                {data?.companyServices?.length ? (
                                    <HStack flexWrap={"wrap"}>
                                        {data?.companyServices?.map((item) => (
                                            <Badge
                                                key={item.id}
                                                variant={"outline"}
                                            >
                                                {item.name}
                                            </Badge>
                                        ))}
                                    </HStack>
                                ) : (
                                    <Box>-</Box>
                                )}
                            </AppRenderDetailItem>
                        )}
                    </Box>
                </Stack>
                <Stack
                    gap={8}
                    direction={{
                        base: "column",
                        sm: "column",
                        md: "row",
                    }}
                >
                    {UTIL.isSchoolType(data?.userType) && (
                        <AppRenderDetailItem
                            label={t(`ent.User:schools.label`)}
                        >
                            <Flex
                                direction="column"
                                maxH={`${3 * 44 - 12}px`}
                                gap={3}
                                overflowY="auto"
                                sx={{ scrollbarWidth: "thin" }}
                            >
                                {data?.schools?.map((item, index) => (
                                    <Text
                                        key={index}
                                        textStyle={"mediumSm"}
                                        bg={"var(--chakra-colors-primaryT90)"}
                                        color={
                                            "var(--chakra-colors-contentT40)"
                                        }
                                        px={2}
                                        lineHeight={"2rem"}
                                        borderRadius={"0.25rem"}
                                    >
                                        {item.name}
                                    </Text>
                                ))}
                            </Flex>
                        </AppRenderDetailItem>
                    )}
                    {UTIL.isCompanyType(data?.userType) && (
                        <AppRenderDetailItem
                            label={t("ent.User:companies.label")}
                        >
                            <Flex
                                direction="column"
                                maxH={`${3 * 44 - 12}px`}
                                gap={3}
                                overflowY="auto"
                                sx={{ scrollbarWidth: "thin" }}
                            >
                                {data?.companies?.map((item, index) => (
                                    <Text
                                        key={index}
                                        textStyle={"mediumSm"}
                                        bg={"var(--chakra-colors-primaryT90)"}
                                        color={
                                            "var(--chakra-colors-contentT40)"
                                        }
                                        px={2}
                                        lineHeight={"2rem"}
                                        borderRadius={"0.25rem"}
                                    >
                                        {item.name}
                                    </Text>
                                ))}
                            </Flex>
                        </AppRenderDetailItem>
                    )}
                </Stack>
                {data?.userType === USERTYPE.USERTYPE_STUDENT && (
                    <>
                        <Stack
                            gap={8}
                            direction={{
                                base: "column",
                                sm: "column",
                                md: "row",
                            }}
                        >
                            <AppRenderDetailItem
                                label={t("ent.User:dob.label")}
                                value={
                                    data?.dob
                                        ? dayjs(data?.dob).format(
                                              APPC.APP_DATE_FORMAT
                                          )
                                        : "-"
                                }
                            />
                            <AppRenderDetailItem
                                label={t("ent.User:nationality.label")}
                                value={data?.nationality}
                            />
                        </Stack>
                        <Stack
                            gap={8}
                            direction={{
                                base: "column",
                                sm: "column",
                                md: "row",
                            }}
                        >
                            <AppRenderDetailItem
                                label={t("ent.User:schoolBatch.label")}
                                value={(data?.schoolBatch as SchoolBatch)?.name}
                            />
                            <AppRenderDetailItem
                                label={t("ent.User:status.label")}
                                value={data?.status}
                            />
                        </Stack>
                        <Stack
                            gap={8}
                            direction={{
                                base: "column",
                                sm: "column",
                                md: "row",
                            }}
                        >
                            <AppRenderDetailItem
                                label={t("ent.User:training.label")}
                                value={(data?.training as Training)?.name}
                            />
                            <AppRenderDetailItem
                                label={t("ent.User:trainingYear.label")}
                                value={`${(data?.trainingYear as TrainingYear)?.code} - ${(data?.trainingYear as TrainingYear)?.year}`}
                            />
                        </Stack>
                        <Stack
                            w={"full"}
                            gap={8}
                            direction={{
                                base: "column",
                                sm: "column",
                                md: "row",
                            }}
                        >
                            <Box w={{ base: "full", sm: "full", md: "50%" }}>
                                <AppRenderDetailItem
                                    label={t("ent.User:address.label")}
                                    value={data?.address}
                                    noOfLines={5}
                                />
                            </Box>
                            <Box w={{ base: "full", sm: "full", md: "50%" }}>
                                <VStack
                                    justifyContent={"space-between"}
                                    gap={8}
                                >
                                    <AppRenderDetailItem
                                        label={t("ent.User:zipCode.label")}
                                        value={data.zipCode}
                                    />
                                    <AppRenderDetailItem
                                        label={t("ent.User:city.label")}
                                        value={data.city}
                                    />
                                </VStack>
                            </Box>
                        </Stack>
                    </>
                )}
                <AppRenderDetailItem label={t("ent.User:isActive.label")}>
                    <HStack>
                        <Badge variant={data?.isActive ? "green" : "red"}>
                            {t(
                                `cmn:label.active.${data?.isActive ? data?.isActive : "null"}`
                            )}
                        </Badge>
                    </HStack>
                </AppRenderDetailItem>
            </VStack>
        </>
    );
};
